import type { Row } from "./api";
import type { InterviewPage } from "./interview-pages";
import type { TaxTopicBinding } from "./tax-topics";
import { mergeTopicEdit } from "./topic-save";
import {
  fieldTargetId,
  validateTaxFields,
  type FieldIssue,
} from "./field-validation";
import { normalizeTaxAge } from "./tax-field-values";
import { applyInterviewCopy } from "./interview-copy";
import { parseFilingInterview, deriveFilingInterview } from "./filing-status";
import {
  filingStatusFieldIssues,
  filingVisibleFields,
  filingLabels,
  type FilingField,
} from "./filing-status-presentation";

const object = (value: unknown): value is Row =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const owns = (value: Row, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);
const clone = <T>(value: T): T =>
  value === undefined ? value : JSON.parse(JSON.stringify(value));

/** Detached page projection: every branch, including the natural-input annotation, keeps the same subset. */
export function pageSchema(page: InterviewPage, card: TaxTopicBinding): Row {
  const schema = applyInterviewCopy(clone(card.schema));
  if (!page.itemIds) return schema;
  const allowed = new Set(page.itemIds);
  const prune = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(prune);
      return;
    }
    if (!object(node)) return;
    const items = node.properties?.items;
    if (object(items) && object(items.properties)) {
      items.properties = Object.fromEntries(
        Object.entries(items.properties).filter(([id]) => allowed.has(id)),
      );
      if (Array.isArray(items.required))
        items.required = items.required.filter(
          (id: string) => allowed.has(id) && owns(items.properties, id),
        );
    }
    Object.values(node).forEach(prune);
  };
  prune(schema);
  return schema;
}

/** Project only master items. Wrapper states, review metadata, and all non-item fields remain unchanged. */
export function pageValue(page: InterviewPage, value: unknown): unknown {
  const projected = clone(value);
  if (!page.itemIds || !object(projected)) return projected;
  const data = owns(projected, "state") ? projected.value : projected;
  if (!object(data) || !object(data.items)) return projected;
  const allowed = new Set(page.itemIds);
  data.items = Object.fromEntries(
    Object.entries(data.items).filter(([id]) => allowed.has(id)),
  );
  return projected;
}

/** Merge the visible edit delta into the newest full draft; absent hidden keys are not deletions. */
export function mergePageValue(
  page: InterviewPage,
  whole: unknown,
  beforeProjected: unknown,
  nextProjected: unknown,
): unknown {
  const merged = mergeTopicEdit(
    whole,
    pageValue(page, beforeProjected),
    pageValue(page, nextProjected),
  );
  if (page.itemIds && object(whole)) {
    const original = owns(whole, "state") ? whole.value : whole;
    const target =
      object(merged) && owns(merged, "state") ? merged.value : merged;
    if (
      object(original?.items) &&
      Object.keys(original.items).some(
        (id) =>
          !page.itemIds!.includes(id) &&
          (!object(target) ||
            !object(target.items) ||
            !owns(target.items, id) ||
            JSON.stringify(original.items[id]) !==
              JSON.stringify(target.items[id])),
      )
    )
      throw new Error(
        `${page.title} — Topic answers: Changing the whole answer would remove information from another page. Review the full topic before replacing it.`,
      );
  }
  return merged;
}

/** A compact, length-framed ASCII root; controls encode it exactly once with fieldTargetId. */
export function pageFieldRoot(
  page: InterviewPage,
  card: TaxTopicBinding,
): string {
  return (
    "interview-" +
    [page.id, card.captureKind, card.record.id]
      .map((value) => {
        const encoded = encodeURIComponent(value);
        return `${encoded.length}_${encoded}`;
      })
      .join("__")
  );
}

function reviewFields(
  target: string,
  maritalSituation: unknown,
): FilingField[] {
  if (["SC-MARRIAGE", "taxpayer.marital_status_year_end"].includes(target))
    return ["maritalSituation"];
  if (target === "SC-SPOUSE-DEATH")
    return [
      maritalSituation === "WIDOWED"
        ? "spouseDeathYear"
        : maritalSituation === "NEVER_MARRIED"
          ? "maritalSituation"
          : "recentSpouseDeath",
    ];
  if (target === "SC-RDP-COMMUNITY") return ["communityIncome"];
  if (target === "SC-HOUSEHOLD")
    return [
      "paidMoreThanHalfHomeCosts",
      "qualifyingPersonName",
      "qualifyingPersonRelationship",
      "qualifyingPersonResidence",
      "qualifyingPersonClaim",
    ];
  if (target === "Q-008" || target === "filingStatus") return ["filingStatus"];
  return [];
}

/** Only controls actually on this page receive errors and focus targets. This is not admission validation. */
export function pageIssues(
  page: InterviewPage,
  card: TaxTopicBinding,
  value: unknown,
  selected: Row,
  previousRaw?: unknown,
): FieldIssue[] {
  if (!card.available || page.reviewOnly) return [];
  const root = pageFieldRoot(page, card);
  const baseline =
    arguments.length >= 5
      ? previousRaw
      : (selected.answers?.["Q-008"] ?? value);
  if (card.record.id === "Q-008" && page.filingFields) {
    const state = parseFilingInterview(value);
    const visible = filingVisibleFields(state, value).filter((field) =>
      page.filingFields!.includes(field),
    );
    const prefix = root + "-review-";
    return filingStatusFieldIssues(
      value,
      root,
      selected,
      baseline === undefined ? null : baseline,
    )
      .flatMap((issue) => {
        if (issue.id.startsWith(prefix)) {
          const field = reviewFields(
            issue.id.slice(prefix.length),
            state.maritalSituation,
          ).find((name) => visible.includes(name));
          if (!field) return [];
          return [
            {
              ...issue,
              path: [field],
              targetId: fieldTargetId(root, [field]),
              fieldLabel: filingLabels[field],
            },
          ];
        }
        return visible.includes(issue.path[0] as FilingField) ? [issue] : [];
      })
      .map((issue) => ({
        ...issue,
        sectionId: page.id,
        sectionLabel: page.title,
      }));
  }
  return validateTaxFields(
    pageSchema(page, card),
    normalizeTaxAge(pageValue(page, value)),
    {
      sectionId: page.id,
      sectionLabel: page.title,
      rootLabel: page.title,
      rootId: root,
    },
  );
}

/** Progress marker only: a captured page can still require a review or an unavailable preparation service. */
export function pageNeedsReview(
  page: InterviewPage,
  card: TaxTopicBinding,
  value: unknown,
  selected: Row,
  previousRaw?: unknown,
): boolean {
  if (!card.available || page.reviewOnly) return true;
  const baseline =
    arguments.length >= 5
      ? previousRaw
      : (selected.answers?.["Q-008"] ?? value);
  if (card.record.id === "Q-008" && page.filingFields) {
    const state = parseFilingInterview(value),
      status = state.filingStatus;
    if (status && status !== "SINGLE") return true;
    if (page.filingFields.includes("filingStatus")) {
      const data = object(value) && owns(value, "state") ? value.value : value;
      const unresolvedSavedReview =
        (object(value) && value.state === "CONFLICT") ||
        (object(data) &&
          object(data.items) &&
          Object.values(data.items).some(
            (item) => item === "CONFLICT" || item === "PRESENT",
          ));
      // Preserve global review visibility without inventing an inline control for a hidden admission check.
      if (
        unresolvedSavedReview ||
        deriveFilingInterview(state, selected, baseline).conflicts.length
      )
        return true;
    }
    return pageIssues(page, card, value, selected, baseline).some(
      (issue) => issue.kind === "invalid",
    );
  }
  if (
    pageIssues(page, card, value, selected, baseline).some(
      (issue) => issue.kind === "invalid",
    )
  )
    return true;
  const projected = pageValue(page, value);
  if (!object(projected)) return false;
  if (["UNKNOWN", "CONFLICT", "PENDING_POSTCALC"].includes(projected.state))
    return true;
  const data = owns(projected, "state") ? projected.value : projected;
  if (
    card.record.id === "Q-009" &&
    object(data) &&
    (data.dependent_count > 0 ||
      data.qualifying_person_count > 0 ||
      data.claimable_status === true)
  )
    return true;
  return (
    object(data) &&
    object(data.items) &&
    Object.values(data.items).some((item) =>
      ["PRESENT", "UNKNOWN", "CONFLICT", "PENDING_POSTCALC"].includes(
        String(item),
      ),
    )
  );
}
